import { DateISO, FormGroup } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { refetchFunc } from 'modules/domain/common/hooks'
import { RespFile } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DatePicker } from 'views/components/DatePicker/DatePicker'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { FormGroupUpload } from 'views/components/FormGroupUpload/FormGroupUpload'
import { ASharedModal, SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal'
import { SampleWithRequests } from 'views/pages/Samples/types'

type Props = {
  sample: SampleWithRequests
  refetch: refetchFunc
  onClose: () => void
}

type Fields = {
  expire_at: DateISO | undefined
  files: RespFile[]
}

const StaticValue = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`

export const CompleteSampleRequest: FC<Props> = ({ onClose, refetch, sample }) => {
  const { t } = useTranslation('samples')
  const { formProgress, formHandler } = useAFormHandler('completeSample', { id: sample.id.toString() })

  const formik = useFormik<Fields>({
    initialValues: {
      expire_at: undefined,
      files: sample.files,
    },

    onSubmit: formHandler(
      async () => {
        await apiClient.put(endpoints.completeSampleRequest(sample.id), formik.values)
      },
      {
        onSuccess: async () => {
          await refetch()
          onClose()
        },
        onError: (error) => {
          formik.setErrors(error.errors)
        },
      },
    ),
  })

  return (
    <ASharedModal size="medium" onClose={onClose} id="editSampleModal">
      <SharedModalTitle>{t('completeSample')}</SharedModalTitle>
      <SharedModalInputs>
        <FormGroup label={t('sampleName')}>
          <StaticValue>{sample.title}</StaticValue>
        </FormGroup>
        <FormGroup label={t('validity')} error={formik.errors.expire_at}>
          <DatePicker
            onChange={(data) => formik.setFieldValue('expire_at', data['date'])}
            value={formik.values.expire_at}
            placeholder={t('validity')}
          />
        </FormGroup>
        <FormGroupUpload
          files={formik.values.files}
          maxWidthFileUpload={405}
          progress={formProgress}
          label={t('file')}
          fieldName="files"
          formik={formik}
        />
      </SharedModalInputs>
      <ADefaultModalFooter
        onConfirm={() => formik.handleSubmit()}
        confirmButtonText={t('common:save')}
        progress={formProgress}
        onClose={onClose}
      />
    </ASharedModal>
  )
}
